import React, { Fragment, useState, useEffect, useRef } from 'react';
import { config } from '_helpers/config';
import { useDispatch } from 'react-redux';
import { FormNav } from './FormNav';
import { toast } from 'react-toastify';
import { docsActions } from '_actions';
import { SignPad } from 'Docs/Components';
import { handleNavigate, navigate } from './formHandlers';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import PropTypes from 'prop-types';
import checkIfDocsShouldBeLocked from '_helpers/lockDocs';
import { preventNegativeValueInDocs } from '_helpers/helpers';
import { Controller, useForm } from 'react-hook-form';
import { autoPopulateSignature } from '../../_helpers/signatureAutoPopulate';

const defaultValues = {
  employee_printed_name: '',
  employee_ssn: '',
  employee_address: '',
  employee_city: '',
  employee_state: '',
  employee_zip: '',
  marriage_status: '',
  regular_withholding_allowances_sheets: null,
  estimated_deductions_allowances_sheets: null,
  total_allowances_sheets: null,
  additional_amount_withheld: null,
  exempt_withholding: '',
  veteran_exempt_withholding: '',
  employee_signature: '',
  signed_date: moment().format('MM/DD/YYYY'),
  employer_name: '',
  employer_address: '',
  payroll_tax_account_number: '',
  your_allowance_a: null,
  spouse_allowance_a: null,
  your_blindness_allowance_a: null,
  spouse_blindness_allowance_a: null,
  dependents_number_a: null,
  total_allowances_a: null,
  itemized_deductions_b: null,
  estimate_deduction_b: null,
  deduction_difference_b: null,
  income_adjustment_b: null,
  income_adjustment_and_deduction_difference_b: null,
  nonwage_income_b: null,
  difference_nonwage_income_b: null,
  nonwage_income_divided_by_1k_b: null,
  finalized_nonwage_income_b: null,
  finalized_deductions_b: null,
  diff_in_deduction_and_nonwage_income_b: null,
  total_wages_estimate_c: null,
  nonwage_income_c: null,
  sum_of_total_wages_and_nonwage_income_c: null,
  itemized_deductions_c: null,
  income_adjustment_c: null,
  sum_of_itemized_deductions_and_income_adjustment_c: null,
  difference_of_income_and_wages_c: null,
  tax_liability_c: null,
  personal_exemptions_c: null,
  difference_personal_exemptions_and_tax_liability_c: null,
  tax_credits_c: null,
  total_tax_liability_c: null,
  tax_withheld_c: null,
  difference_of_tax_withheld_and_liability_c: null,
  difference_divided_by_pay_periods: null,
};

const taxBrackets = {
  single_or_married_filing_separately: [
    { min: 0, max: 10756, rate: 0.011, base: 0, baseTax: 0 },
    { min: 10756, max: 25499, rate: 0.022, base: 10756, baseTax: 118.32 },
    { min: 25499, max: 40245, rate: 0.044, base: 25499, baseTax: 442.67 },
    { min: 40245, max: 55866, rate: 0.066, base: 40245, baseTax: 1091.49 },
    { min: 55866, max: 70606, rate: 0.088, base: 55866, baseTax: 2122.48 },
    { min: 70606, max: 360659, rate: 0.1023, base: 70606, baseTax: 3419.6 },
    { min: 360659, max: 432787, rate: 0.1133, base: 360659, baseTax: 33092.02 },
    { min: 432787, max: 721314, rate: 0.1243, base: 432787, baseTax: 41264.12 },
    {
      min: 721314,
      max: 1000000,
      rate: 0.1353,
      base: 721314,
      baseTax: 77128.03,
    },
    {
      min: 1000000,
      max: Infinity,
      rate: 0.1463,
      base: 1000000,
      baseTax: 114834.25,
    },
  ],
  married_filing_jointly_or_qualifying_widow: [
    { min: 0, max: 21512, rate: 0.011, base: 0, baseTax: 0 },
    { min: 21512, max: 50998, rate: 0.022, base: 21512, baseTax: 236.63 },
    { min: 50998, max: 80490, rate: 0.044, base: 50998, baseTax: 885.32 },
    { min: 80490, max: 111732, rate: 0.066, base: 80490, baseTax: 2182.97 },
    { min: 111732, max: 141212, rate: 0.088, base: 111732, baseTax: 4244.94 },
    { min: 141212, max: 721318, rate: 0.1023, base: 141212, baseTax: 6839.18 },
    { min: 721318, max: 865574, rate: 0.1133, base: 721318, baseTax: 66184.02 },
    {
      min: 865574,
      max: 1000000,
      rate: 0.1243,
      base: 865574,
      baseTax: 82528.22,
    },
    {
      min: 1000000,
      max: 1442628,
      rate: 0.1353,
      base: 1000000,
      baseTax: 99237.37,
    },
    {
      min: 1442628,
      max: Infinity,
      rate: 0.1463,
      base: 1442628,
      baseTax: 159124.94,
    },
  ],
  head_of_household: [
    { min: 0, max: 21527, rate: 0.011, base: 0, baseTax: 0 },
    { min: 21527, max: 51000, rate: 0.022, base: 21527, baseTax: 236.8 },
    { min: 51000, max: 65744, rate: 0.044, base: 51000, baseTax: 885.21 },
    { min: 65744, max: 81364, rate: 0.066, base: 65744, baseTax: 1533.95 },
    { min: 81364, max: 96107, rate: 0.088, base: 81364, baseTax: 2564.87 },
    { min: 96107, max: 490493, rate: 0.1023, base: 96107, baseTax: 3862.25 },
    { min: 490493, max: 588593, rate: 0.1133, base: 490493, baseTax: 44207.94 },
    { min: 588593, max: 980987, rate: 0.1243, base: 588593, baseTax: 55322.67 },
    {
      min: 980987,
      max: 1000000,
      rate: 0.1353,
      base: 980987,
      baseTax: 104097.24,
    },
    {
      min: 1000000,
      max: Infinity,
      rate: 0.1463,
      base: 1000000,
      baseTax: 106669.7,
    },
  ],
};

const fieldsToCalculateTotalAllowanceA = [
  'your_allowance_a',
  'spouse_allowance_a',
  'your_blindness_allowance_a',
  'spouse_blindness_allowance_a',
  'dependents_number_a',
];

const allowedKeysRestrictionInput = [
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'Delete',
];

export default function WithholdingAllowanceCertificate({
  adminMode,
  mobileMode,
  lastPage,
  rehire,
  setScreen,
  isSingleDoc,
  doc,
  docs,
  resetForm,
  ...props
}) {
  const dispatch = useDispatch();
  const pages = [1, 2, 3];
  const [page, setPage] = useState(1);
  const [formOwner, setFormOwner] = useState('');
  const [isDocumentLocked, setDocumentLocked] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    control,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const watchedValues = watch([
    'your_allowance_a',
    'spouse_allowance_a',
    'your_blindness_allowance_a',
    'spouse_blindness_allowance_a',
    'dependents_number_a',
    'itemized_deductions_b',
    'estimate_deduction_b',
    'income_adjustment_b',
    'nonwage_income_b',
    'total_wages_estimate_c',
    'tax_credits_c',
    'tax_withheld_c',
  ]);

  useEffect(() => {
    const values = getValues();

    const round = (num) => {
      // Convert num to a number first
      const validNum = !isNaN(Number(num)) ? Number(num) : 0;
      return Number(validNum.toFixed(2));
    };

    // Worksheet A calculations
    if (fieldsToCalculateTotalAllowanceA.some((key) => values[key])) {
      const totalA = fieldsToCalculateTotalAllowanceA.reduce(
        (sum, key) => sum + (parseInt(values[key]) || 0),
        0,
      );
      setValue('total_allowances_a', totalA);
      setValue('regular_withholding_allowances_sheets', totalA);
      setValue(
        'total_allowances_sheets',
        totalA + (parseInt(values.estimated_deductions_allowances_sheets) || 0),
      );
    } else {
      setValue('total_allowances_a', null);
      setValue('regular_withholding_allowances_sheets', null);
      setValue(
        'total_allowances_sheets',
        parseInt(values.estimated_deductions_allowances_sheets) || 0,
      );
    }

    const nonwage = round(parseFloat(values.nonwage_income_b));
    // Worksheet B calculations
    if (
      values.itemized_deductions_b ||
      values.estimate_deduction_b ||
      values.income_adjustment_b
    ) {
      const deductionDiff = round(
        parseFloat(values.itemized_deductions_b) -
          parseFloat(values.estimate_deduction_b),
      );
      setValue('deduction_difference_b', deductionDiff);

      const incomeAdjustAndDeduction = round(
        deductionDiff + parseFloat(values.income_adjustment_b),
      );
      setValue(
        'income_adjustment_and_deduction_difference_b',
        incomeAdjustAndDeduction,
      );

      if (incomeAdjustAndDeduction > nonwage) {
        const diff = round(incomeAdjustAndDeduction - nonwage);
        const nonWageDividedBy1k = Math.round(diff / 1000);
        setValue('difference_nonwage_income_b', diff);
        setValue('nonwage_income_divided_by_1k_b', nonWageDividedBy1k);
        setValue('estimated_deductions_allowances_sheets', nonWageDividedBy1k);
        setValue(
          'total_allowances_sheets',
          (parseInt(values.regular_withholding_allowances_sheets) || 0) +
            nonWageDividedBy1k,
        );
        setValue('finalized_nonwage_income_b', null);
        setValue('finalized_deductions_b', null);
        setValue('diff_in_deduction_and_nonwage_income_b', null);
      } else {
        setValue('difference_nonwage_income_b', null);
        setValue('nonwage_income_divided_by_1k_b', null);
        setValue('estimated_deductions_allowances_sheets', null);
        setValue(
          'total_allowances_sheets',
          parseInt(values.regular_withholding_allowances_sheets) || 0,
        );
        setValue('finalized_nonwage_income_b', nonwage);
        setValue('finalized_deductions_b', incomeAdjustAndDeduction);
        setValue(
          'diff_in_deduction_and_nonwage_income_b',
          round(nonwage - incomeAdjustAndDeduction),
        );
      }
    } else {
      [
        'deduction_difference_b',
        'income_adjustment_and_deduction_difference_b',
        'finalized_nonwage_income_b',
        'finalized_deductions_b',
        'diff_in_deduction_and_nonwage_income_b',
        'difference_nonwage_income_b',
        'nonwage_income_divided_by_1k_b',
        'estimated_deductions_allowances_sheets',
      ].forEach((field) => setValue(field, null));
      setValue(
        'total_allowances_sheets',
        parseInt(values.regular_withholding_allowances_sheets) || 0,
      );
    }
  }, [watchedValues]);

  const totalWagesEstimateC = watch('total_wages_estimate_c');
  const taxCreditsC = watch('tax_credits_c');
  const taxWithheldC = watch('tax_withheld_c');
  const differenceDividedByPayPeriods = watch(
    'difference_divided_by_pay_periods',
  );

  useEffect(() => {
    const values = getValues();
    const round = (num) => {
      // Convert num to a number first
      const validNum = !isNaN(Number(num)) ? Number(num) : 0;
      return Number(validNum.toFixed(2));
    };

    const nonwage = round(parseFloat(values.nonwage_income_b));

    if (values.difference_divided_by_pay_periods) {
      setValue(
        'additional_amount_withheld',
        values.difference_divided_by_pay_periods,
      );
    } else {
      setValue('additional_amount_withheld', null);
    }

    // Worksheet C calculations
    if (values.total_wages_estimate_c) {
      const sumOfTotalWagesAndNonWageIncome = round(
        parseFloat(values.total_wages_estimate_c) + nonwage,
      );

      const itemizedDeductions = round(
        Math.max(
          parseFloat(values.itemized_deductions_b) || 0,
          parseFloat(values.estimate_deduction_b) || 0,
        ),
      );

      const incomeAdjustment = round(parseFloat(values.income_adjustment_b));
      const sumOfItemizedDeductionsAndIncomeAdjustment = round(
        incomeAdjustment + itemizedDeductions,
      );

      const differenceOfIncomeAndWages = round(
        sumOfTotalWagesAndNonWageIncome -
          sumOfItemizedDeductionsAndIncomeAdjustment,
      );

      const taxLiability = round(calculateTax(differenceOfIncomeAndWages));
      const personalExemptions = round(
        (parseFloat(values.total_allowances_a) || 0) * 163.9,
      );

      const diffInPersonalExemptionsAndTaxLiability = round(
        taxLiability - personalExemptions,
      );

      const totalTaxLiability = round(
        diffInPersonalExemptionsAndTaxLiability -
          parseFloat(values.tax_credits_c || 0),
      );

      const differenceOfTaxWithheldAndLiability = round(
        totalTaxLiability - parseFloat(values.tax_withheld_c || 0),
      );

      const updatedValues = {
        nonwage_income_c: nonwage,
        sum_of_total_wages_and_nonwage_income_c:
          sumOfTotalWagesAndNonWageIncome,
        itemized_deductions_c: itemizedDeductions,
        income_adjustment_c: incomeAdjustment,
        sum_of_itemized_deductions_and_income_adjustment_c:
          sumOfItemizedDeductionsAndIncomeAdjustment,
        difference_of_income_and_wages_c: differenceOfIncomeAndWages,
        tax_liability_c: taxLiability,
        personal_exemptions_c: personalExemptions,
        difference_personal_exemptions_and_tax_liability_c:
          diffInPersonalExemptionsAndTaxLiability,
        total_tax_liability_c: totalTaxLiability,
        difference_of_tax_withheld_and_liability_c:
          differenceOfTaxWithheldAndLiability,
      };

      Object.entries(updatedValues).forEach(([key, val]) => {
        setValue(key, val, { shouldValidate: false, shouldDirty: false });
      });
    } else {
      [
        'nonwage_income_c',
        'sum_of_total_wages_and_nonwage_income_c',
        'itemized_deductions_c',
        'income_adjustment_c',
        'sum_of_itemized_deductions_and_income_adjustment_c',
        'difference_of_income_and_wages_c',
        'tax_liability_c',
        'personal_exemptions_c',
        'difference_personal_exemptions_and_tax_liability_c',
        'total_tax_liability_c',
        'difference_of_tax_withheld_and_liability_c',
      ].forEach((field) => {
        setValue(field, null, { shouldValidate: false, shouldDirty: false });
      });
    }
  }, [
    totalWagesEstimateC,
    taxCreditsC,
    taxWithheldC,
    differenceDividedByPayPeriods,
    page,
  ]);

  useEffect(() => {
    const value = watch('estimate_deduction_b');
    if (value && ![11080, 5540].includes(Number(value))) {
      setError('estimate_deduction_b', {
        type: 'manual',
        message: 'Value must be 11080 or 5540',
      });
    } else {
      clearErrors('estimate_deduction_b');
    }
  }, [watch('estimate_deduction_b')]);

  const handleSignature = (field, signatureData) => {
    setValue(field, signatureData, { shouldValidate: true });
  };

  const skipHandler = () => {
    let data = {
      id: doc?.id,
      doc_type: doc?.docs_type_id,
      skippedPages: [1],
    };

    if (adminMode) {
      data.admin_mode = adminMode;
    }

    if (rehire) {
      data.rehire = true;
    }

    dispatch(docsActions.saveDoc(data)).then((res) => {
      if (!res.success) {
        return toast.error(res.message);
      }

      if (mobileMode && !lastPage) {
        setScreen('sidebar');
        navigate('next', rehire, props?.giss, {
          props: { docs, adminMode, resetForm, ...props },
        });
      } else if (lastPage) {
        if (adminMode) {
          props.history.push('/docs');
        } else {
          props.history.push('/dashboard');
        }
      } else {
        navigate('next', rehire, props?.giss, {
          props: { docs, adminMode, resetForm, ...props },
        });
      }
    });
  };

  const calculateTax = (income) => {
    const marriage_status = watch('marriage_status');
    if (!marriage_status) {
      return 0;
    }
    const brackets = taxBrackets[marriage_status];
    const bracket = brackets.find((b) => income > b.min && income <= b.max);
    if (!bracket) return 0;
    return ((income - bracket.base) * bracket.rate + bracket.baseTax).toFixed(
      2,
    );
  };

  const getStatusLabel = (key) => {
    switch (key) {
      case 'single_or_married_filing_separately':
        return 'Single Persons, Dual Income Married or Married With Multiple Employers';
      case 'married_filing_jointly_or_qualifying_widow':
        return 'Married Persons';
      case 'head_of_household':
        return 'Unmarried / Head of Household';
      default:
        return key;
    }
  };

  const onSubmit = (formData) => {
    formData.signed_date =
      formData.signed_date || moment().format('MM/DD/YYYY');

    const {
      additional_amount_withheld,
      exempt_withholding,
      veteran_exempt_withholding,
    } = formData;

    const filledFields = [
      additional_amount_withheld,
      exempt_withholding,
      veteran_exempt_withholding,
    ].filter(
      (value) => value != null && value !== '' && !isNaN(value) && value,
    );

    if (filledFields.length > 1) {
      toast.error(
        'Only one of the three withholding fields should be filled on Page 1.',
      );
      return;
    }
    if (page < pages.length) {
      return navigatePage('next');
    }

    let data = {
      id: doc?.id,
      doc_type: doc?.docs_type_id,
      form: formData,
      single_doc: isSingleDoc ? true : false,
      rehire: rehire ? true : false,
    };

    dispatch(docsActions.saveDoc(data)).then((res) => {
      if (res.success) {
        if (mobileMode && !lastPage) {
          setScreen('sidebar');
          navigate('next', rehire, props?.giss, {
            props: { docs, adminMode, resetForm, ...props },
          });
        } else if (lastPage) {
          if (adminMode) {
            props.history.push('/docs');
          } else {
            props.history.push('/dashboard');
          }
        } else {
          navigate('next', rehire, props?.giss, {
            props: { docs, adminMode, resetForm, ...props },
          });
        }
      } else {
        toast.error(res.message);
      }
    });
  };

  // Single useEffect for form loading and auto-population
  useEffect(() => {
    if (doc) {
      dispatch(docsActions.getForm(doc?.id))
        .then((res) => {
          if (res.success) {
            const formValuesData = res?.data?.form || {};
            const userData = res?.data?.user || {};
            setFormOwner(res?.data?.form_owner);

            const fullName = [
              userData.first_name,
              userData.middle_name,
              userData.last_name,
            ]
              .filter(Boolean)
              .join(' ');

            // Use reset to populate the entire form with values
            reset({
              ...formValuesData,
              signed_date: moment().format('MM/DD/YYYY'),
              employee_printed_name: formValuesData.employee_printed_name
                ? formValuesData.employee_printed_name
                : fullName,
              employee_ssn: formValuesData.employee_ssn
                ? formValuesData.employee_ssn
                : userData.ssn,
            });

            // Auto-populate signature after form is reset with a small delay
            setTimeout(() => {
              if (!formValuesData.employee_signature) {
                autoPopulateSignature(
                  formValuesData,
                  (signature) => {
                    setValue('employee_signature', signature, {
                      shouldValidate: true,
                    });
                  },
                  'employee_signature',
                );
              }
            }, 100);
          }
        })
        .catch((err) => {
          toast.error(err?.message ?? 'Error on loading data!', {
            position: 'top-right',
            autoClose: 5000,
          });
        });
    }
  }, []);


  const navigatePage = (to) => {
    switch (to) {
      case 'next':
        setPage(page + 1);
        return;
      case 'prev':
        setPage(page - 1);
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    const formValues = getValues();
    const shouldLockDocs = checkIfDocsShouldBeLocked(
      formOwner,
      formValues,
      adminMode,
    );
    setDocumentLocked(shouldLockDocs);
  }, [formOwner, adminMode]);

  return (
    <div className='doc_info-rh form-w4'>
      {/* Title */}
      <div className='doc-title'>
        <img
          src={`${config.assetUrl}icons/doc-title-img.svg`}
          className='doc-title-img'
        />
        <span className='close-button' onClick={() => setScreen('sidebar')}>
          <img src={`${config.assetUrl}icons/back-btn.svg`} />
        </span>
        <h3 style={{ width: '50%' }}>
          Employee's Withholding Allowance Certificate &nbsp;&nbsp;
        </h3>

        <div
          className={
            isSingleDoc ? 'doc-page-count left_non_auto' : 'doc-page-count'
          }
        >
          <span className='current_page'>{page}</span>
          <span>of {pages.length}</span>
        </div>
      </div>

      <div className='custom-pagination-nav'>
        <div className='pagination-wrapper'>
          {page > 1 && (
            <a
              href='#'
              className='previous'
              onClick={() => {
                navigatePage('prev');
              }}
            >
              <svg
                width='6'
                height='11'
                viewBox='0 0 6 11'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M5.41406 10.6094L5.88281 10.1641C5.97656 10.0469 5.97656 9.85938 5.88281 9.76562L1.64062 5.5L5.88281 1.25781C5.97656 1.16406 5.97656 0.976562 5.88281 0.859375L5.41406 0.414062C5.29688 0.296875 5.13281 0.296875 5.01562 0.414062L0.09375 5.3125C0 5.42969 0 5.59375 0.09375 5.71094L5.01562 10.6094C5.13281 10.7266 5.29688 10.7266 5.41406 10.6094Z'
                  fill='black'
                  fill-opacity='0.87'
                />
              </svg>
            </a>
          )}

          <p>
            Page {page} <span>of {pages.length}</span>
          </p>
          {page < pages.length && (
            <a
              href='#'
              className='next'
              onClick={() => {
                navigatePage('next');
              }}
            >
              <svg
                width='6'
                height='11'
                viewBox='0 0 6 11'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M0.5625 0.414062L0.09375 0.859375C0 0.976562 0 1.16406 0.09375 1.25781L4.33594 5.5L0.09375 9.76562C0 9.85938 0 10.0469 0.09375 10.1641L0.5625 10.6094C0.679688 10.7266 0.84375 10.7266 0.960938 10.6094L5.88281 5.71094C5.97656 5.59375 5.97656 5.42969 5.88281 5.3125L0.960938 0.414062C0.84375 0.296875 0.679688 0.296875 0.5625 0.414062Z'
                  fill='black'
                  fill-opacity='0.8'
                />
              </svg>
            </a>
          )}
        </div>
      </div>
      <div className='doc_info-form'>
        <span className='opt_text'>OPTIONAL</span>
        <br />
        <form
          className='info-form'
          onSubmit={handleSubmit(onSubmit)}
          name='withholding-allowance-certificate'
        >
          <fieldset disabled={isDocumentLocked}>
            {page === 1 && (
              <>
                <div className='row'>
                  <p>
                    Complete this form so that your employer can withhold the
                    correct California state income tax from your pay.
                  </p>
                  <h4>Personal Information</h4>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        First, Middle, Last Name
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items group'>
                        <div className='grouped'>
                          <input
                            {...register('employee_printed_name', {
                              required: 'First name is required',
                              maxLength: {
                                value: 100,
                                message:
                                  'Length exceeds the maximum limit of 100 characters.',
                              },
                            })}
                            className={`${
                              errors.employee_printed_name ? 'error' : ''
                            }`}
                            type='text'
                            id='employee_printed_name'
                            field='employee_printed_name'
                            placeholder='Full Name'
                          />
                          {errors.employee_printed_name && (
                            <label
                              htmlFor='employee_printed_name'
                              className='error_label'
                            >
                              {errors.employee_printed_name.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        SSN
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items'>
                        <input
                          {...register('employee_ssn', {
                            required: 'SSN is required',
                            minLength: {
                              value: 9,
                              message: 'SSN must be 9 digits long',
                            },
                            maxLength: {
                              value: 9,
                              message: 'SSN must be exactly 9 digits',
                            },
                            pattern: {
                              value: /^\d{9}$/,
                              message:
                                'SSN must be numeric and exactly 9 digits',
                            },
                          })}
                          className={`${errors.employee_ssn ? 'error' : ''}`}
                          type='number'
                          id='employee_ssn'
                          field='employee_ssn'
                          placeholder='SSN'
                        />
                        {errors.employee_ssn && (
                          <label htmlFor='employee_ssn' className='error_label'>
                            {errors.employee_ssn.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        Address
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items'>
                        <input
                          {...register('employee_address', {
                            required: 'Address is required',
                            maxLength: {
                              value: 100,
                              message:
                                'Length exceeds the maximum limit of 100 characters.',
                            },
                          })}
                          className={`${
                            errors.employee_address ? 'error' : ''
                          }`}
                          type='text'
                          id='employee_address'
                          field='employee_address'
                          placeholder='Address'
                        />
                        {errors.employee_address && (
                          <label
                            htmlFor='employee_address'
                            className='error_label'
                          >
                            {errors.employee_address.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-items group'>
                        <div className='grouped'>
                          <input
                            {...register('employee_city', {
                              required: 'City is required',
                              maxLength: {
                                value: 100,
                                message:
                                  'Length exceeds the maximum limit of 100 characters.',
                              },
                            })}
                            className={`${errors.employee_city ? 'error' : ''}`}
                            type='text'
                            id='employee_city'
                            field='employee_city'
                            placeholder='City'
                          />
                          {errors.employee_city && (
                            <label
                              htmlFor='employee_city'
                              className='error_label'
                            >
                              {errors.employee_city.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='inputs-col'>
                      <div className='inputs-items'>
                        <input
                          {...register('employee_state', {
                            required: 'State is required',
                            maxLength: {
                              value: 100,
                              message:
                                'Length exceeds the maximum limit of 100 characters.',
                            },
                          })}
                          className={`${errors.employee_state ? 'error' : ''}`}
                          type='text'
                          id='employee_state'
                          field='employee_state'
                          placeholder='State'
                        />
                        {errors.employee_state && (
                          <label
                            htmlFor='employee_state'
                            className='error_label'
                          >
                            {errors.employee_state.message}
                          </label>
                        )}
                      </div>
                    </div>
                    <div className='inputs-col'>
                      <div className='inputs-items'>
                        <input
                          {...register('employee_zip', {
                            required: 'ZIP code is required',
                            pattern: {
                              value: /^\d{5}(-\d{4})?$/,
                              message:
                                'Invalid ZIP code format (use 12345 or 12345-6789)',
                            },
                          })}
                          className={`${errors.employee_zip ? 'error' : ''}`}
                          type='string'
                          id='employee_zip'
                          field='employee_zip'
                          placeholder='Zip Code'
                        />
                        {errors.employee_zip && (
                          <label htmlFor='employee_zip' className='error_label'>
                            {errors.employee_zip.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        Filing Status
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items'>
                        <select
                          {...register('marriage_status', {
                            required: 'Marriage status is required',
                          })}
                          className={`${errors.marriage_status ? 'error' : ''}`}
                          id='marriage_status'
                          field='marriage_status'
                        >
                          <option value=''>- Select -</option>
                          <option value='single_or_married_filing_separately'>
                            Single or Married (with two or more incomes)
                          </option>
                          <option value='married_filing_jointly_or_qualifying_widow'>
                            Married (one income)
                          </option>
                          <option value='head_of_household'>
                            Head of Household
                          </option>
                        </select>
                        {errors.marriage_status && (
                          <label
                            htmlFor='marriage_status'
                            className='error_label'
                          >
                            {errors.marriage_status.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row form-row worksheet no_border no_padding'>
                  <p>
                    1. Use Worksheet A for Regular Withholding allowances. Use
                    other worksheets on the following pages as applicable.
                  </p>
                  <ol style={{ listStyle: 'none' }}>
                    <li>
                      <div className='col mb-1'>
                        <div className='inputs-col mbp-1'>
                          <div className='inputs-head'>
                            1a. &nbsp; Number of Regular Withholding Allowances
                            <b className='highlight'> (Worksheet A)</b>
                          </div>
                          <div className='inputs-items'>
                            <input
                              {...register(
                                'regular_withholding_allowances_sheets',
                                {
                                  min: {
                                    value: 0,
                                    message: 'Value must be at least 0',
                                  },
                                },
                              )}
                              disabled
                              type='number'
                              placeholder='- Enter here -'
                              className={`${
                                errors.regular_withholding_allowances_sheets
                                  ? 'error'
                                  : ''
                              } price_input`}
                              id='regular_withholding_allowances_sheets'
                              field='regular_withholding_allowances_sheets'
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={preventNegativeValueInDocs}
                            />
                            {errors.regular_withholding_allowances_sheets && (
                              <label
                                htmlFor='regular_withholding_allowances_sheets'
                                className='error_label'
                              >
                                {
                                  errors.regular_withholding_allowances_sheets
                                    .message
                                }
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className='col mb-1'>
                        <div className='inputs-col mbp-1'>
                          <div className='inputs-head'>
                            1b. &nbsp; Number of allowances from the Estimated
                            Deductions <b className='highlight'> (Worksheet B)</b>
                          </div>
                          <div className='inputs-items'>
                            <input
                              {...register(
                                'estimated_deductions_allowances_sheets',
                                {
                                  min: {
                                    value: 0,
                                    message: 'Value must be at least 0',
                                  },
                                },
                              )}
                              disabled
                              type='number'
                              placeholder='- Enter here -'
                              className={`${
                                errors.estimated_deductions_allowances_sheets
                                  ? 'error'
                                  : ''
                              } price_input`}
                              id='estimated_deductions_allowances_sheets'
                              field='estimated_deductions_allowances_sheets'
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={preventNegativeValueInDocs}
                            />
                            {errors.estimated_deductions_allowances_sheets && (
                              <label
                                htmlFor='estimated_deductions_allowances_sheets'
                                className='error_label'
                              >
                                {
                                  errors.estimated_deductions_allowances_sheets
                                    .message
                                }
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className='col mb-1'>
                        <div className='inputs-col mbp-1'>
                          <div className='inputs-head'>
                            1c. &nbsp; Total Number of Allowances you are
                            claiming
                          </div>
                          <div className='inputs-items'>
                            <input
                              {...register('total_allowances_sheets', {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              })}
                              disabled
                              type='number'
                              className={`${
                                errors.total_allowances_sheets ? 'error' : ''
                              } price_input`}
                              id='total_allowances_sheets'
                              field='total_allowances_sheets'
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={preventNegativeValueInDocs}
                            />
                            {errors.total_allowances_sheets && (
                              <label
                                htmlFor='total_allowances_sheets'
                                className='error_label'
                              >
                                {errors.total_allowances_sheets.message}
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  </ol>
                  <div className='col mb-1'>
                    <div className='inputs-col mbp-1'>
                      <div className='inputs-head'>
                        2. Additional amount, if any, you want withheld each pay
                        period (if employer agrees),{' '}
                        <b className='highlight'> (Worksheet C)</b>
                      </div>
                      <div className='inputs-items'>
                        <input
                          {...register('additional_amount_withheld', {
                            min: {
                              value: 0,
                              message: 'Value must be at least 0',
                            },
                          })}
                          disabled
                          type='number'
                          placeholder='- Enter here -'
                          className={`${
                            errors.additional_amount_withheld ? 'error' : ''
                          } price_input`}
                          id='additional_amount_withheld'
                          field='additional_amount_withheld'
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={preventNegativeValueInDocs}
                        />
                        {errors.additional_amount_withheld && (
                          <label
                            htmlFor='additional_amount_withheld'
                            className='error_label'
                          >
                            {errors.additional_amount_withheld.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  <p>OR</p>
                  <h4>Exemption from Withholding</h4>
                  <div className='col mb-1'>
                    <div className='inputs-col mbp-1'>
                      <div className='inputs-head'>
                        3. I claim exemption from withholding for 2025, and I
                        certify I meet both conditions for exemption.
                      </div>
                      <div
                        className='inputs-items'
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingTop: '0.5rem',
                        }}
                      >
                        <input
                          {...register('exempt_withholding')}
                          style={{
                            cursor: 'pointer',
                            width: '16px',
                            height: '16px',
                          }}
                          type='checkbox'
                          id='exempt_withholding'
                          field='exempt_withholding'
                        />
                      </div>
                    </div>
                  </div>
                  <p>OR</p>
                  <div className='col mb-1'>
                    <div className='inputs-col mbp-1'>
                      <div className='inputs-head'>
                        4. I certify under penalty of perjury that I am{' '}
                        <b className='highlight'> not subject</b> to California
                        withholding. I meet the conditions set forth under the
                        Service Member Civil Relief Act, as amended by the
                        Military Spouses Residency Relief Act and the Veterans
                        Benefits and Transition Act of 2018.
                      </div>
                      <div
                        className='inputs-items'
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingTop: '0.5rem',
                        }}
                      >
                        <input
                          {...register('veteran_exempt_withholding')}
                          style={{
                            cursor: 'pointer',
                            width: '16px',
                            height: '16px',
                          }}
                          type='checkbox'
                          field='veteran_exempt_withholding'
                          id='veteran_exempt_withholding'
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row form-row'>
                  <p>
                    Under penalty of perjury, I certify that the number of
                    withholding allowances claimed on this certificate does not
                    exceed the number to which I am entitled or, if claiming
                    exemption from withholding, that I am entitled to claim the
                    exempt status.
                  </p>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        Employee's Signature
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items'>
                        <div className='sign_here'>
                          <Fragment>
                            <SignPad
                              id='employee_signature'
                              height={214}
                              width={446}
                              signature={watch('employee_signature')}
                              handleSignature={(sign) =>
                                handleSignature('employee_signature', sign)
                              }
                            />
                            <input
                              {...register('employee_signature', {
                                required: 'Signature is required',
                              })}
                              type='hidden'
                              name='employee_signature'
                              field='employee_signature'
                            ></input>
                            {errors.employee_signature && (
                              <label
                                htmlFor='employee_signature'
                                className='error_label'
                              >
                                {errors.employee_signature.message}
                              </label>
                            )}
                          </Fragment>
                        </div>
                      </div>
                    </div>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        Date<span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items'>
                        <Controller
                          name='signed_date'
                          control={control}
                          rules={{
                            required: 'Signed Date is required',
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <DatePicker
                              selected={
                                value ? moment(value, 'MM/DD/YYYY') : moment()
                              }
                              onChange={(date) =>
                                onChange(
                                  date ? moment(date).format('MM/DD/YYYY') : '',
                                )
                              }
                              className={`${
                                error ? 'error' : ''
                              } w-100`}
                              name='signed_date'
                              id='signed_date'
                              field='signed_date'
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode='select'
                              autoComplete='off'
                              disabled
                              readOnly
                            />
                          )}
                        />
                        {errors.signed_date && (
                          <label htmlFor='signed_date' className='error_label'>
                            {errors.signed_date.message}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='row form-row'>
                  <h4>Employer's Section</h4>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        Employer's Name and Address
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items group'>
                        <div className='grouped'>
                          <input
                            {...register('employer_name', {
                              maxLength: {
                                value: 100,
                                message:
                                  'Length exceeds the maximum limit of 100 characters.',
                              },
                            })}
                            className={`${errors.employer_name ? 'error' : ''}`}
                            type='textarea'
                            name='employer_name'
                            id='employer_name'
                            field='employer_name'
                            placeholder='Full Name'
                            disabled={!adminMode}
                          />
                          {errors.employer_name && (
                            <label
                              htmlFor='employer_name'
                              className='error_label'
                            >
                              {errors.employer_name.message}
                            </label>
                          )}

                          <input
                            {...register('employer_address', {
                              maxLength: {
                                value: 100,
                                message:
                                  'Length exceeds the maximum limit of 100 characters.',
                              },
                            })}
                            className={`${errors.employer_address ? 'error' : ''}`}
                            type='textarea'
                            name='employer_address'
                            id='employer_address'
                            field='employer_address'
                            placeholder='Address'
                            disabled={!adminMode}
                          />
                          {errors.employer_address && (
                            <label
                              htmlFor='employer_address'
                              className='error_label'
                            >
                              {errors.employer_address.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='inputs-col'>
                      <div className='inputs-head'>
                        California Employer Payroll Tax Account Number
                        <span className='required-star'>&#x2A;</span>
                      </div>
                      <div className='inputs-items group'>
                        <div className='grouped'>
                          <input
                            {...register('payroll_tax_account_number', {
                              pattern: {
                                value: /^\d{8}(-\d)?$/,
                                message:
                                  'Format must be 8 digits or 8 digits followed by -digit (e.g., 12345678 or 12345678-1)',
                              },
                            })}
                            className={`${
                              errors.payroll_tax_account_number ? 'error' : ''
                            }`}
                            type='string'
                            name='payroll_tax_account_number'
                            id='payroll_tax_account_number'
                            field='payroll_tax_account_number'
                            placeholder='12345678 or 12345678-1'
                            disabled={!adminMode}
                          />
                          {errors.payroll_tax_account_number && (
                            <label
                              htmlFor='payroll_tax_account_number'
                              className='error_label'
                            >
                              {errors.payroll_tax_account_number.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='row form-row'>
                  <p>
                    The Employee's Withholding Allowance Certificate (DE 4) is
                    for{' '}
                    <b className='highlight'>
                      {' '}
                      California Personal Income Tax (PIT)
                    </b>{' '}
                    withholding purposes only. The DE 4 is used to compute the
                    amount of taxes to be withheld from your wages, by your
                    employer, to accurately reflect your state tax withholding
                    obligation.
                  </p>
                  <p>
                    As of January 1, 2020, the Employee's Withholding Allowance
                    Certificate (Form W-4) from the Internal Revenue Service
                    (IRS) is used for federal income tax withholding{' '}
                    <b className='highlight'>only</b>. You must file the state form
                    DE 4 to determine the appropriate California PIT
                    withholding.
                  </p>
                  <p>
                    If you do not provide your employer a completed DE 4, your
                    employer must use Single with Zero withholding allowance.
                  </p>
                  <p>
                    <b className='highlight'>Check Your Withholding:</b> After your
                    DE 4 takes effect, compare the state income tax withheld
                    with your estimated total annual tax. For state withholding,
                    use the worksheets on this form.
                  </p>
                  <p>
                    <b className='highlight'>Exemption From Withholding:</b> If you
                    wish to claim exempt, complete the federal Form W-4 and the
                    state DE 4. You may claim exempt from withholding California
                    income tax if you meet both of the following conditions for
                    exemption:
                  </p>
                  <ol>
                    <li>
                      You did not owe any federal and state income tax last
                      year, and
                    </li>
                    <li>
                      You do not expect to owe any federal and state income tax
                      this year.{' '}
                    </li>
                  </ol>
                  <p>
                    If you continue to qualify for the exempt filing status, a
                    new DE 4 designating <b className='highlight'>exempt</b> must be
                    submitted by February 15 each year to continue your
                    exemption. If you are not having federal and state income
                    tax withheld this year but expect to have a tax liability
                    next year, you are required to give your employer a new DE 4
                    by December 1.
                  </p>
                  <p>
                    <b className='highlight'>Member Service Civil Relief Act:</b>{' '}
                    Under this act, as provided by the Military Spouses
                    Residency Relief Act and the Veterans Benefits and
                    Transition Act of 2018, you may be exempt from California
                    income tax withholding on your wages if
                  </p>
                  <ol type='i'>
                    <li>
                      Your spouse is a member of the armed forces present in
                      California in compliance with military orders;
                    </li>
                    <li>
                      You are present in California solely to be with your
                      spouse; and
                    </li>
                    <li>You maintain your domicile in another state.</li>
                  </ol>
                  <p>
                    If you claim exemption under this act,{' '}
                    <b className='highlight'>check the box on Line 4</b>. You may be
                    required to provide proof of exemption upon request.
                  </p>
                </div>

                <div className='row form-row'>
                  <div className='col'>
                    <div className='disclaimers'>
                      <p>
                        The{' '}
                        <a
                          href='https://edd.ca.gov/siteassets/files/pdf_pub_ctr/de44.pdf'
                          target='_blank'
                        >
                          California Employer's Guide (DE 44)
                        </a>{' '}
                        (edd.ca.gov/pdf_pub_ctr/de44.pdf) provides the income
                        tax withholding tables. This publication can be found by
                        visiting{' '}
                        <a
                          href='https://edd.ca.gov/en/Payroll_Taxes/Forms_and_Publications'
                          target='_blank'
                        >
                          Payroll Taxes - Forms and Publications
                        </a>{' '}
                        (edd.ca.gov/Payroll_Taxes/Forms_and_Publications.htm).
                        To assist you in calculating your tax liability, visit
                        the{' '}
                        <a href='https://ftb.ca.gov/' target='_blank'>
                          Franchise Tax Board (FTB)
                        </a>{' '}
                        (ftb.ca.gov).
                      </p>
                      <p>
                        If you need information on your last California Resident
                        Income Tax Return (FTB Form 540), visit the{' '}
                        <a href='https://ftb.ca.gov/' target='_blank'>
                          FTB
                        </a>{' '}
                        (ftb.ca.gov).
                      </p>
                      <p>
                        <b className='highlight'>Notification:</b> The burden of
                        proof rests with the employee to show the correct
                        California income tax withholding. Pursuant to section
                        4340-1(e) of{' '}
                        <a
                          href='https://govt.westlaw.com/calregs/Search/Index'
                          target='_blank'
                        >
                          Title 22, California Code of Regulations (CCR)
                        </a>{' '}
                        (govt.westlaw.com/calregs/Search/Index), the FTB or the
                        EDD may require an employer to submit a Form W-4 or DE 4
                        when such forms are necessary for the administration of
                        the withholding tax programs.
                      </p>
                      <p>
                        <b className='highlight'>Penalty:</b> You may be fined $500
                        if you file, with no reasonable basis, a DE 4 that
                        results in less tax being withheld than is properly
                        allowable. Criminal penalties apply for willfully
                        supplying false or fraudulent information or failing to
                        supply information requiring an increase in withholding.
                        This is provided by section 13101 of the{' '}
                        <a
                          href='http://leginfo.legislature.ca.gov/faces/codes.xhtml'
                          target='_blank'
                        >
                          California Unemployment Insurance Code
                        </a>{' '}
                        (leginfo.legislature.ca.gov/faces/codes.xhtml) and
                        section 19176 of the{' '}
                        <a
                          href='http://leginfo.legislature.ca.gov/faces/codes.xhtml'
                          target='_blank'
                        >
                          Revenue and Taxation Code
                        </a>{' '}
                        (leginfo.legislature.ca.gov/faces/codes.xhtml).
                      </p>
                    </div>
                  </div>
                </div>
                <FormNav
                  currentPage={page}
                  noOfPages={pages.length}
                  navigate={(to) =>
                    handleNavigate(to, rehire, docs, doc, props)
                  }
                  adminMode={adminMode}
                  skip={true}
                  skipHandler={skipHandler}
                  disclaimer_check={true}
                  single_doc={isSingleDoc}
                  lastPage={false}
                  mobileMode={mobileMode}
                />
              </>
            )}

            {page === 2 && (
              <div className='row form-row worksheet no_border no_padding custom-margin'>
                <h3>Worksheets</h3>
                <hr />
                <h5>Instructions — 1 — Allowances*</h5>
                <p>
                  When determining your withholding allowances, you must
                  consider your personal situation:
                </p>
                <ul>
                  <li>Do you claim allowances for dependents or blindness?</li>
                  <li>Will you itemize your deductions?</li>
                  <li>
                    Do you have more than one income coming into the household?
                  </li>
                </ul>
                <p>
                  <b className='highlight'>Two-Earners or Multiple Incomes:</b> When
                  earnings come from more than one source, under-withholding may
                  occur. If you have a working spouse or more than one job, it
                  is best to check the box "Single or Married (with two or more
                  incomes)." Figure the total number of allowances you are
                  entitled to claim on all jobs using only one DE 4 form. Claim
                  allowances with <b className='highlight'>one</b> employer.
                </p>
                <p>
                  Do <b className='highlight'>not</b> claim the same allowances with
                  more than one employer. Your withholding will usually be most
                  accurate when all allowances are claimed on the DE 4 filed for
                  the highest paying job and zero allowances are claimed for the
                  others.
                </p>
                <p>
                  <b className='highlight'>
                    Married But Not Living With Your Spouse:
                  </b>{' '}
                  You may check the "Head of Household" marital status box if
                  you meet all of the following:
                </p>
                <ol>
                  <li>
                    Your spouse will not live with you{' '}
                    <b className='highlight'>at any time</b> during the year;
                  </li>
                  <li>
                    You will furnish over half of the cost of maintaining a home
                    for the entire year for yourself and your child or stepchild
                    who qualifies as your dependent;{' '}
                    <b className='highlight'>and</b>
                  </li>
                  <li>You will file a separate return for the year.</li>
                </ol>
                <p>
                  <b className='highlight'>Head of Household:</b> To qualify, you
                  must be unmarried or legally separated from your spouse and
                  pay more than 50 percent of the costs of maintaining a home
                  for the <b className='highlight'>entire</b> year for yourself and
                  your dependent(s) or other qualifying individuals. Cost of
                  maintaining the home includes such items as rent, property
                  insurance, property taxes, mortgage interest, repairs,
                  utilities, and cost of food. It does not include the
                  individual's personal expenses or any amount which represents
                  value of services performed by a member of the household of
                  the taxpayer.
                </p>
                <hr />
                <h4>Worksheet A - Regular Withholding Allowances</h4>
                <hr />
                <ol type='A'>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Allowance for yourself — enter 1
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('your_allowance_a', {
                              validate: (value) =>
                                value == 1 ||
                                value === '' ||
                                Number.isNaN(value) ||
                                'Only the value 1 is allowed',
                            })}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => {
                              !allowedKeysRestrictionInput.includes(e.key) &&
                                (e.key !== '1' || e.target.value.length >= 1) &&
                                e.preventDefault();
                            }}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.your_allowance_a && 'error'
                            } price_input`}
                            id='your_allowance_a'
                            field='your_allowance_a'
                          />
                          {errors.your_allowance_a && (
                            <label
                              htmlFor='your_allowance_a'
                              className='error_label'
                            >
                              {errors.your_allowance_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Allowance for your spouse (if not separately claimed
                          by your spouse) — enter 1
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('spouse_allowance_a', {
                              validate: (value) =>
                                value == 1 ||
                                value === '' ||
                                Number.isNaN(value) ||
                                'Only the value 1 is allowed',
                            })}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => {
                              !allowedKeysRestrictionInput.includes(e.key) &&
                                (e.key !== '1' || e.target.value.length >= 1) &&
                                e.preventDefault();
                            }}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.spouse_allowance_a && 'error'
                            } price_input`}
                            name='spouse_allowance_a'
                            id='spouse_allowance_a'
                            field='spouse_allowance_a'
                          />
                          {errors.spouse_allowance_a && (
                            <label
                              htmlFor='spouse_allowance_a'
                              className='error_label'
                            >
                              {errors.spouse_allowance_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Allowance for blindness — yourself — enter 1
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('your_blindness_allowance_a', {
                              validate: (value) =>
                                value == 1 ||
                                value === '' ||
                                Number.isNaN(value) ||
                                'Only the value 1 is allowed',
                            })}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => {
                              !allowedKeysRestrictionInput.includes(e.key) &&
                                (e.key !== '1' || e.target.value.length >= 1) &&
                                e.preventDefault();
                            }}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.your_blindness_allowance_a && 'error'
                            } price_input`}
                            name='your_blindness_allowance_a'
                            id='your_blindness_allowance_a'
                            field='your_blindness_allowance_a'
                          />
                          {errors.your_blindness_allowance_a && (
                            <label
                              htmlFor='your_blindness_allowance_a'
                              className='error_label'
                            >
                              {errors.your_blindness_allowance_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Allowance for blindness — your spouse (if not
                          separately claimed by your spouse) — enter 1
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('spouse_blindness_allowance_a', {
                              validate: (value) =>
                                value == 1 ||
                                value === '' ||
                                Number.isNaN(value) ||
                                'Only the value 1 is allowed',
                            })}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => {
                              !allowedKeysRestrictionInput.includes(e.key) &&
                                (e.key !== '1' || e.target.value.length >= 1) &&
                                e.preventDefault();
                            }}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.spouse_blindness_allowance_a && 'error'
                            } price_input`}
                            name='spouse_blindness_allowance_a'
                            id='spouse_blindness_allowance_a'
                            field='spouse_blindness_allowance_a'
                          />
                          {errors.spouse_blindness_allowance_a && (
                            <label
                              htmlFor='spouse_blindness_allowance_a'
                              className='error_label'
                            >
                              {errors.spouse_blindness_allowance_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Allowance(s) for dependent(s) — do not include
                          yourself or your spouse
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('dependents_number_a', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 99,
                                message: 'Value must be 99 or less',
                              },
                              validate: {
                                isIntegerOrEmpty: (value) => {
                                  if (value === '') return true;
                                  const num = Number(value);
                                  return Number.isInteger(num)
                                    ? true
                                    : 'Only whole numbers are allowed';
                                },
                              },
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.dependents_number_a && 'error'
                            } price_input`}
                            name='dependents_number_a'
                            id='dependents_number_a'
                            field='dependents_number_a'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.dependents_number_a && (
                            <label
                              htmlFor='dependents_number_a'
                              className='error_label'
                            >
                              {errors.dependents_number_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Total — add lines (A) through (E) above and enter on
                          line 1a of the DE 4
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('total_allowances_a', {})}
                            type='number'
                            className={`${
                              errors.total_allowances_a && 'error'
                            } price_input`}
                            id='total_allowances_a'
                            field='total_allowances_a'
                            onWheel={(e) => e.target.blur()}
                            disabled
                          />
                          {errors.total_allowances_a && (
                            <label
                              htmlFor='total_allowances_a'
                              className='error_label'
                            >
                              {errors.total_allowances_a.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                </ol>
                <hr />
                <h4>
                  Instructions - 2 - Additional Withholding Allowances
                  (Optional)
                </h4>
                <p>
                  If you expect to itemize deductions on your California income
                  tax return, you can claim additional withholding allowances.
                  Use Worksheet B to determine whether your expected estimated
                  deductions may entitle you to claim{' '}
                  <b className='highlight'>one or more additional </b>
                  withholding allowances. Use last year's FTB Form 540 as a
                  model to calculate this year's withholding amounts.
                </p>
                <p>
                  Do not include deferred compensation, qualified pension
                  payments, or flexible benefits, etc., that are deducted from
                  your gross pay but are not taxed on this worksheet.
                </p>
                <p>
                  You may reduce the amount of tax withheld from your wages by
                  claiming one additional withholding allowance for each $1,000,
                  or fraction of $1,000, by which you expect your estimated
                  deductions for the year to exceed your allowable standard
                  deduction.
                </p>
                <hr />
                <h4>Worksheet B - Estimated Deductions</h4>
                <p>
                  Use this worksheet <b className='highlight'>only</b> if you plan
                  to itemize deductions, claim certain adjustments to income, or
                  have a large amount of nonwage income not subject to
                  withholding.
                </p>
                <hr />
                <ol>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter an estimate of your itemized deductions for
                          California taxes for this tax year as listed in the
                          schedules in the FTB Form 540
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('itemized_deductions_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 1000000,
                                message:
                                  'Please enter an estimate under $1,000,000',
                              },
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.itemized_deductions_b && 'error'
                            } price_input`}
                            id='itemized_deductions_b'
                            field='itemized_deductions_b'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.itemized_deductions_b && (
                            <label
                              htmlFor='itemized_deductions_b'
                              className='error_label'
                            >
                              {errors.itemized_deductions_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter $11,080 if married filing joint with two or more
                          allowances, unmarried head of household, or qualifying
                          widow(er) with dependent(s) or $5,540 if single or
                          married filing separately, dual income married, or
                          married with multiple employers
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('estimate_deduction_b', {
                              validate: (value) =>
                                value == 11080 ||
                                value == 5540 ||
                                value == '' ||
                                'Value must be 11080 or 5540',
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.estimate_deduction_b && 'error'
                            } price_input`}
                            id='estimate_deduction_b'
                            field='estimate_deduction_b'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.estimate_deduction_b && (
                            <label
                              htmlFor='estimate_deduction_b'
                              className='error_label'
                            >
                              {errors.estimate_deduction_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 2 from line 1, enter difference
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('deduction_difference_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.deduction_difference_b && 'error'
                            } price_input`}
                            id='deduction_difference_b'
                            field='deduction_difference_b'
                            disabled
                          />
                          {errors.deduction_difference_b && (
                            <label
                              htmlFor='deduction_difference_b'
                              className='error_label'
                            >
                              {errors.deduction_difference_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter an estimate of your adjustments to income
                          (alimony payments, IRA deposits)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('income_adjustment_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 500000,
                                message:
                                  'Please enter an estimate under $500,000',
                              },
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.income_adjustment_b && 'error'
                            } price_input`}
                            id='income_adjustment_b'
                            field='income_adjustment_b'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.income_adjustment_b && (
                            <label
                              htmlFor='income_adjustment_b'
                              className='error_label'
                            >
                              {errors.income_adjustment_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Add line 4 to line 3, enter sum
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'income_adjustment_and_deduction_difference_b',
                              {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              },
                            )}
                            type='number'
                            className={`${
                              errors.income_adjustment_and_deduction_difference_b &&
                              'error'
                            } price_input`}
                            id='income_adjustment_and_deduction_difference_b'
                            field='income_adjustment_and_deduction_difference_b'
                            disabled
                          />
                          {errors.income_adjustment_and_deduction_difference_b && (
                            <label
                              htmlFor='income_adjustment_and_deduction_difference_b'
                              className='error_label'
                            >
                              {
                                errors
                                  .income_adjustment_and_deduction_difference_b
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter an estimate of your nonwage income (dividends,
                          interest income, alimony receipts)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('nonwage_income_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 1000000,
                                message:
                                  'Please enter an estimate under $1,000,000',
                              },
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.nonwage_income_b && 'error'
                            } price_input`}
                            id='nonwage_income_b'
                            field='nonwage_income_b'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.nonwage_income_b && (
                            <label
                              htmlFor='nonwage_income_b'
                              className='error_label'
                            >
                              {errors.nonwage_income_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          If line 5 is greater than line 6 (if less, see below
                          [go to line 9]); Subtract line 6 from line 5, enter
                          difference
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('difference_nonwage_income_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.difference_nonwage_income_b && 'error'
                            } price_input`}
                            id='difference_nonwage_income_b'
                            field='difference_nonwage_income_b'
                            disabled
                          />
                          {errors.difference_nonwage_income_b && (
                            <label
                              htmlFor='difference_nonwage_income_b'
                              className='error_label'
                            >
                              {errors.difference_nonwage_income_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Divide the amount on line 7 by $1,000, round any
                          fraction to the nearest whole number enter this number
                          on line 1b of the DE 4. Complete Worksheet C, if
                          needed, otherwise <b className='highlight'>stop here</b>.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('nonwage_income_divided_by_1k_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.nonwage_income_divided_by_1k_b && 'error'
                            } price_input`}
                            id='nonwage_income_divided_by_1k_b'
                            field='nonwage_income_divided_by_1k_b'
                            disabled
                          />
                          {errors.nonwage_income_divided_by_1k_b && (
                            <label
                              htmlFor='nonwage_income_divided_by_1k_b'
                              className='error_label'
                            >
                              {errors.nonwage_income_divided_by_1k_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          If line 6 is greater than line 5; Enter amount from
                          line 6 (nonwage income)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('finalized_nonwage_income_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.finalized_nonwage_income_b && 'error'
                            } price_input`}
                            id='finalized_nonwage_income_b'
                            field='finalized_nonwage_income_b'
                            disabled
                          />
                          {errors.finalized_nonwage_income_b && (
                            <label
                              htmlFor='finalized_nonwage_income_b'
                              className='error_label'
                            >
                              {errors.finalized_nonwage_income_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter amount from line 5 (deductions)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('finalized_deductions_b', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.finalized_deductions_b && 'error'
                            } price_input`}
                            id='finalized_deductions_b'
                            field='finalized_deductions_b'
                            disabled
                          />
                          {errors.finalized_deductions_b && (
                            <label
                              htmlFor='finalized_deductions_b'
                              className='error_label'
                            >
                              {errors.finalized_deductions_b.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 10 from line 9, enter difference. Then,
                          complete Worksheet C.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'diff_in_deduction_and_nonwage_income_b',
                              {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              },
                            )}
                            type='number'
                            className={`${
                              errors.diff_in_deduction_and_nonwage_income_b &&
                              'error'
                            } price_input`}
                            name='diff_in_deduction_and_nonwage_income_b'
                            id='diff_in_deduction_and_nonwage_income_b'
                            field='diff_in_deduction_and_nonwage_income_b'
                            disabled
                          />
                          {errors.diff_in_deduction_and_nonwage_income_b && (
                            <label
                              htmlFor='diff_in_deduction_and_nonwage_income_b'
                              className='error_label'
                            >
                              {
                                errors.diff_in_deduction_and_nonwage_income_b
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                </ol>
                <p>
                  *Wages paid to registered domestic partners will be treated
                  the same for state income tax purposes as wages paid to
                  spouses for California PIT withholding and PIT wages. This law
                  does not impact federal income tax law. A registered domestic
                  partner means an individual partner in a domestic partner
                  relationship within the meaning of section 297 of the Family
                  Code. For more information, call our Taxpayer Assistance
                  Center at <a href='tel:8887453886'>1-888-745-3886</a>.
                </p>

                <FormNav
                  currentPage={page}
                  noOfPages={pages.length}
                  navigate={(to) => navigatePage(to)}
                  adminMode={adminMode}
                  skip={false}
                  skipHandler={skipHandler}
                  disclaimer_check={true}
                  single_doc={isSingleDoc}
                  lastPage={false}
                  mobileMode={mobileMode}
                />
              </div>
            )}
            {page === 3 && (
              <div className='row form-row worksheet no_border no_padding custom-margin'>
                <h4>
                  Worksheet C - Additional Tax Withholding and Estimated Tax
                </h4>
                <hr />
                <ol>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter estimate of total wages for tax year 2025
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('total_wages_estimate_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 1000000,
                                message:
                                  'Please enter an estimate under $1,000,000',
                              },
                            })}
                            type='number'
                            placeholder='- Enter here -'
                            className={`${
                              errors.total_wages_estimate_c && 'error'
                            } price_input`}
                            id='total_wages_estimate_c'
                            field='total_wages_estimate_c'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.total_wages_estimate_c && (
                            <label
                              htmlFor='total_wages_estimate_c'
                              className='error_label'
                            >
                              {errors.total_wages_estimate_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter estimate of nonwage income (line 6 of Worksheet
                          B)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('nonwage_income_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.nonwage_income_c && 'error'
                            } price_input`}
                            id='nonwage_income_c'
                            field='nonwage_income_c'
                            disabled
                          />
                          {errors.nonwage_income_c && (
                            <label
                              htmlFor='nonwage_income_c'
                              className='error_label'
                            >
                              {errors.nonwage_income_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Add line 1 and line 2. Enter sum.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'sum_of_total_wages_and_nonwage_income_c',
                              {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              },
                            )}
                            type='number'
                            className={`${
                              errors.sum_of_total_wages_and_nonwage_income_c &&
                              'error'
                            } price_input`}
                            id='sum_of_total_wages_and_nonwage_income_c'
                            field='sum_of_total_wages_and_nonwage_income_c'
                            disabled
                          />
                          {errors.sum_of_total_wages_and_nonwage_income_c && (
                            <label
                              htmlFor='sum_of_total_wages_and_nonwage_income_c'
                              className='error_label'
                            >
                              {
                                errors.sum_of_total_wages_and_nonwage_income_c
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter itemized deductions or standard deduction (line
                          1 or 2 of Worksheet B, whichever is largest).
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('itemized_deductions_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.itemized_deductions_c && 'error'
                            } price_input`}
                            id='itemized_deductions_c'
                            field='itemized_deductions_c'
                            disabled
                          />
                          {errors.itemized_deductions_c && (
                            <label
                              htmlFor='itemized_deductions_c'
                              className='error_label'
                            >
                              {errors.itemized_deductions_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter adjustments to income (line 4 of Worksheet B)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('income_adjustment_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.income_adjustment_c && 'error'
                            } price_input`}
                            id='income_adjustment_c'
                            field='income_adjustment_c'
                            disabled
                          />
                          {errors.income_adjustment_c && (
                            <label
                              htmlFor='income_adjustment_c'
                              className='error_label'
                            >
                              {errors.income_adjustment_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Add line 4 and line 5. Enter sum.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'sum_of_itemized_deductions_and_income_adjustment_c',
                              {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              },
                            )}
                            type='number'
                            className={`${
                              errors.sum_of_itemized_deductions_and_income_adjustment_c &&
                              'error'
                            } price_input`}
                            id='sum_of_itemized_deductions_and_income_adjustment_c'
                            field='sum_of_itemized_deductions_and_income_adjustment_c'
                            disabled
                          />
                          {errors.sum_of_itemized_deductions_and_income_adjustment_c && (
                            <label
                              htmlFor='sum_of_itemized_deductions_and_income_adjustment_c'
                              className='error_label'
                            >
                              {
                                errors
                                  .sum_of_itemized_deductions_and_income_adjustment_c
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 6 from line 3. Enter difference.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('difference_of_income_and_wages_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.difference_of_income_and_wages_c && 'error'
                            } price_input`}
                            id='difference_of_income_and_wages_c'
                            field='difference_of_income_and_wages_c'
                            disabled
                          />
                          {errors.difference_of_income_and_wages_c && (
                            <label
                              htmlFor='difference_of_income_and_wages_c'
                              className='error_label'
                            >
                              {errors.difference_of_income_and_wages_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Figure your tax liability for the amount on line 7 by
                          using the 2025 tax rate schedules below.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('tax_liability_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.tax_liability_c && 'error'
                            } price_input`}
                            id='tax_liability_c'
                            field='tax_liability_c'
                            disabled
                          />
                          {errors.tax_liability_c && (
                            <label
                              htmlFor='tax_liability_c'
                              className='error_label'
                            >
                              {errors.tax_liability_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter personal exemptions (line F of Worksheet A x
                          $163.90)
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('personal_exemptions_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.personal_exemptions_c && 'error'
                            } price_input`}
                            id='personal_exemptions_c'
                            field='personal_exemptions_c'
                            disabled
                          />
                          {errors.personal_exemptions_c && (
                            <label
                              htmlFor='personal_exemptions_c'
                              className='error_label'
                            >
                              {errors.personal_exemptions_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 9 from line 8. Enter difference
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'difference_personal_exemptions_and_tax_liability_c',
                              {
                                min: {
                                  value: 0,
                                  message: 'Value must be at least 0',
                                },
                              },
                            )}
                            type='number'
                            className={`${
                              errors.difference_personal_exemptions_and_tax_liability_c &&
                              'error'
                            } price_input`}
                            id='difference_personal_exemptions_and_tax_liability_c'
                            field='difference_personal_exemptions_and_tax_liability_c'
                            disabled
                          />
                          {errors.difference_personal_exemptions_and_tax_liability_c && (
                            <label
                              htmlFor='difference_personal_exemptions_and_tax_liability_c'
                              className='error_label'
                            >
                              {
                                errors
                                  .difference_personal_exemptions_and_tax_liability_c
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Enter any tax credits. (See FTB Form 540).
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('tax_credits_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: watch(
                                  'difference_personal_exemptions_and_tax_liability_c',
                                ),
                                message:
                                  'Value must be lesser than difference between personal exemptions and tax liability',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.tax_credits_c && 'error'
                            } price_input`}
                            id='tax_credits_c'
                            field='tax_credits_c'
                            placeholder='- Enter here -'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.tax_credits_c && (
                            <label
                              htmlFor='tax_credits_c'
                              className='error_label'
                            >
                              {errors.tax_credits_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 11 from line 10. Enter difference. This
                          is your total tax liability.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('total_tax_liability_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.total_tax_liability_c && 'error'
                            } price_input`}
                            id='total_tax_liability_c'
                            field='total_tax_liability_c'
                            disabled
                          />
                          {errors.total_tax_liability_c && (
                            <label
                              htmlFor='total_tax_liability_c'
                              className='error_label'
                            >
                              {errors.total_tax_liability_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Calculate the tax withheld and estimated to be
                          withheld during 2025. Contact your employer to request
                          the amount that will be withheld on your wages based
                          on the marital status and number of withholding
                          allowances you will claim for 2025. Multiply the
                          estimated amount to be withheld by the number of pay
                          periods left in the year. Add the total to the amount
                          already withheld for 2025.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('tax_withheld_c', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: watch('total_wages_estimate_c'),
                                message:
                                  'Value must be lesser than estimate of total wages',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.tax_withheld_c && 'error'
                            } price_input`}
                            id='tax_withheld_c'
                            field='tax_withheld_c'
                            placeholder='- Enter here -'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.tax_withheld_c && (
                            <label
                              htmlFor='tax_withheld_c'
                              className='error_label'
                            >
                              {errors.tax_withheld_c.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Subtract line 13 from line 12. Enter difference. If
                          this is less than zero, you do not need to have
                          additional taxes withheld.
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register(
                              'difference_of_tax_withheld_and_liability_c',
                              {},
                            )}
                            type='number'
                            className={`${
                              errors.difference_of_tax_withheld_and_liability_c &&
                              'error'
                            } price_input`}
                            id='total_tax_liability_c'
                            field='total_tax_liability_c'
                            disabled
                          />
                          {errors.difference_of_tax_withheld_and_liability_c && (
                            <label
                              htmlFor='difference_of_tax_withheld_and_liability_c'
                              className='error_label'
                            >
                              {
                                errors
                                  .difference_of_tax_withheld_and_liability_c
                                  .message
                              }
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='col mb-1'>
                      <div className='inputs-col mbp-1'>
                        <div className='inputs-head'>
                          Divide line 14 by the number of pay periods remaining
                          in the year. Enter this figure on line 2 of the DE 4
                        </div>
                        <div className='inputs-items'>
                          <input
                            {...register('difference_divided_by_pay_periods', {
                              min: {
                                value: 0,
                                message: 'Value must be at least 0',
                              },
                              max: {
                                value: 200000,
                                message: 'Value must be lesser than $200,000',
                              },
                            })}
                            type='number'
                            className={`${
                              errors.difference_divided_by_pay_periods &&
                              'error'
                            } price_input`}
                            id='difference_divided_by_pay_periods'
                            field='difference_divided_by_pay_periods'
                            placeholder='- Enter here -'
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={preventNegativeValueInDocs}
                          />
                          {errors.difference_divided_by_pay_periods && (
                            <label
                              htmlFor='difference_divided_by_pay_periods'
                              className='error_label'
                            >
                              {errors.difference_divided_by_pay_periods.message}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                </ol>
                <p>
                  <b className='highlight'>Note:</b> Your employer is not required
                  to withhold the additional amount requested on line 2 of your
                  DE 4. If your employer does not agree to withhold the
                  additional amount, you may increase your withholdings as much
                  as possible by using the "single" status with "zero"
                  allowances. If the amount withheld still results in an
                  underpayment of state income taxes, you may need to file
                  quarterly estimates on Form 540-ES with the FTB to avoid a
                  penalty.
                </p>
                <p>
                  <i>
                    These Tables Are for Calculating Worksheet C and for 2025
                    Only
                  </i>
                </p>
                {Object.entries(taxBrackets).map(([statusKey, brackets]) => (
                  <div key={statusKey} className='mb-6'>
                    <h3 className='font-bold text-lg capitalize'>
                      {getStatusLabel(statusKey)}
                    </h3>
                    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <table
                        style={{ minWidth: '600px', width: '100%' }}
                        className='border mt-1 mb-2 w-full border-collapse tax-table'
                      >
                        <tbody>
                          <tr>
                            <th colSpan={2}>If the Taxable Income is</th>
                            <th colSpan={3}>Computed Tax is</th>
                          </tr>
                          <tr>
                            <td className='border p-2'>Over</td>
                            <td className='border p-2'>But Not Over</td>
                            <td colSpan={2} className='border p-2'>
                              Of Amount Over...
                            </td>
                            <td className='border p-2'>Plus</td>
                          </tr>
                          {brackets.map((bracket, i) => (
                            <tr key={i}>
                              <td className='border p-2'>
                                ${bracket.min.toLocaleString()}
                              </td>
                              <td className='border p-2'>
                                {bracket.max === Infinity
                                  ? 'and over'
                                  : `$${bracket.max.toLocaleString()}`}
                              </td>
                              <td className='border p-2'>
                                {(bracket.rate * 100).toFixed(3)}%
                              </td>
                              <td className='border p-2'>
                                ${bracket.min.toLocaleString()}
                              </td>
                              <td className='border p-2'>
                                ${bracket.baseTax.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                <p>
                  If you need information on your last California Resident
                  Income Tax Return, FTB Form 540, visit{' '}
                  <a href='https://www.ftb.ca.gov' target='_blank'>
                    FTB
                  </a>{' '}
                  (ftb.ca.gov).
                </p>
                <hr />
                <p>
                  The DE 4 information is collected for purposes of
                  administering the PIT law and under the authority of Title 22,
                  CCR, section 4340-1, and the California Revenue and Taxation
                  Code, including section 18624. The Information Practices Act
                  of 1977 requires that individuals be notified of how
                  information they provide may be used. More information is in
                  the instructions that came with your last California resident
                  income tax return.
                </p>

                <FormNav
                  currentPage={page}
                  noOfPages={pages.length}
                  navigate={(to) => navigatePage(to)}
                  adminMode={adminMode}
                  skip={false}
                  skipHandler={skipHandler}
                  disclaimer_check={true}
                  single_doc={isSingleDoc}
                  lastPage={false}
                  mobileMode={mobileMode}
                />
              </div>
            )}
          </fieldset>
        </form>
      </div>
    </div>
  );
}

WithholdingAllowanceCertificate.propTypes = {
  single_doc: PropTypes.bool,
  disclaimer_check: PropTypes.bool,
  docs: PropTypes.object.isRequired,
  lastPage: PropTypes.bool,
  adminMode: PropTypes.bool,
  skip: PropTypes.bool,
  selectedUser: PropTypes.object,
  isExtraDoc: PropTypes.bool,
  otherDocMode: PropTypes.bool,
  match: PropTypes.object.isRequired,
  mobileMode: PropTypes.bool,
  skipHandler: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  noOfPages: PropTypes.number.isRequired,
  setScreen: PropTypes.func,
  isSingleDoc: PropTypes.bool,
  doc: PropTypes.string,
  resetForm: PropTypes.func,
  history: PropTypes.object,
  giss: PropTypes.object,
  rehire: PropTypes.bool,
  toggleErrorPopup: PropTypes.func.isRequired,
  toggleCloseConfirmation: PropTypes.func.isRequired,
  view_doc: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  currentUserInfo: PropTypes.object.isRequired,
  toggle: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
}
